import { Box, Flex, Text, Image, Icon } from "@chakra-ui/react";
import { FaThLarge, FaBriefcase, FaTasks, FaRegClock, FaUsers, FaFileAlt, FaCalendarAlt } from "react-icons/fa";
import bobLockupDark from "../assets/logo/bob/bob-lockup-dark.svg";

// A static, non-interactive recreation of the real BOB dashboard (see the
// screenshots this was built from) — used only as a homepage teaser image.
// The full, scroll-driven, six-screen walkthrough lives on /bob
// (src/pages/bob/BobDemo.tsx); this is intentionally a separate, simpler
// component rather than a shared one, since it never needs to animate.

const NAV = [
  { label: "Áttekintés", icon: FaThLarge, active: true },
  { label: "Partnerek", icon: FaBriefcase },
  { label: "Feladatok", icon: FaTasks },
  { label: "Jelenlét", icon: FaRegClock },
  { label: "Munkatársak", icon: FaUsers },
  { label: "Dokumentumok", icon: FaFileAlt },
];

const TILES = [
  { label: "Aktív partnerek", value: "18", swatch: "bob.100", iconColor: "bob.300" },
  { label: "Munkatársak", value: "12", swatch: "#E7E1F7", iconColor: "#7C6FD6" },
  { label: "Nyitott feladatok", value: "7", swatch: "#FCE8C6", iconColor: "#B8860B" },
  { label: "Lejárt feladatok", value: "1", swatch: "#FCE1D5", iconColor: "#C0442B" },
];

const DEADLINES = [
  { title: "Árajánlat véglegesítése", who: "Eddie Employee · szept. 10.", tone: "Közepes" },
  { title: "Szerződés-hosszabbítás", who: "Mia Manager · szept. 5.", tone: "Magas" },
];

const RECENT = [
  { title: "Ajánlat elküldve", who: "Zöldkert Kft. · aug. 26." },
  { title: "Egyeztetés lezajlott", who: "Napfény Ingatlan Zrt. · aug. 22." },
];

const BobPreviewCard = () => (
  <Box
    borderRadius="2xl"
    overflow="hidden"
    border="1px solid"
    borderColor="chalk.400"
    boxShadow="0 24px 70px rgba(19, 78, 74, 0.16)"
    bg="white"
  >
    <Flex align="stretch">
      {/* Sidebar */}
      <Flex
        direction="column"
        w={{ base: "76px", sm: "160px" }}
        flexShrink={0}
        position="relative"
        overflow="hidden"
        bgGradient="linear(to-b, bob.800, bob.900)"
        px={{ base: 2, sm: 4 }}
        py={5}
      >
        <Image src={bobLockupDark} alt="bob" w={{ base: "34px", sm: "68px" }} mb={0.5} position="relative" zIndex={1} />
        <Text
          display={{ base: "none", sm: "block" }}
          fontSize="6px"
          fontWeight="700"
          letterSpacing="0.1em"
          color="whiteAlpha.500"
          textTransform="uppercase"
          mb={4}
          position="relative"
          zIndex={1}
        >
          Üzleti működés alapja
        </Text>
        <Box h="1px" bg="whiteAlpha.200" mb={4} position="relative" zIndex={1} display={{ base: "none", sm: "block" }} />
        <Box mb={4} position="relative" zIndex={1} display={{ base: "none", sm: "block" }}>
          <Text fontSize="8px" fontWeight="700" letterSpacing="0.06em" color="whiteAlpha.500" textTransform="uppercase" mb={0.5}>
            Munkaterület
          </Text>
          <Text fontSize="xs" fontWeight="700" color="white" noOfLines={1}>
            Acme Demo Corp
          </Text>
        </Box>
        <Flex direction="column" gap={1} position="relative" zIndex={1}>
          {NAV.map((item) => (
            <Flex
              key={item.label}
              align="center"
              justify={{ base: "center", sm: "flex-start" }}
              gap={2}
              px={{ base: 0, sm: 2.5 }}
              py={1.5}
              borderRadius="md"
              fontSize="9px"
              fontWeight={item.active ? "700" : "500"}
              color={item.active ? "bob.300" : "whiteAlpha.700"}
              bg={item.active ? "rgba(20, 184, 166, 0.14)" : "transparent"}
            >
              <Icon as={item.icon} boxSize="9px" flexShrink={0} />
              <Text noOfLines={1} display={{ base: "none", sm: "block" }}>
                {item.label}
              </Text>
            </Flex>
          ))}
        </Flex>

        <Box position="absolute" left="-10px" bottom="-10px" w="120px" h="100px" opacity={0.45} zIndex={0} display={{ base: "none", sm: "block" }}>
          <svg viewBox="0 0 160 140" width="100%" height="100%" fill="none">
            <path d="M8 128 L48 78 L82 100 L128 60" stroke="#14b8a6" strokeOpacity="0.35" strokeWidth="1.5" fill="none" />
            {[
              [8, 128],
              [48, 78],
              [82, 100],
              [128, 60],
            ].map(([cx, cy]) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" fill="#14b8a6" fillOpacity="0.6" />
            ))}
          </svg>
        </Box>
      </Flex>

      {/* Content */}
      <Box flex="1" minW={0}>
        <Flex justify="flex-end" align="center" px={{ base: 3, sm: 5 }} py={2.5} borderBottom="1px solid" borderColor="chalk.300">
          <Box
            fontSize="9px"
            px={2}
            py={0.5}
            border="1px solid"
            borderColor="bob.300"
            borderRadius="full"
            color="bob.300"
            mr={2}
            display={{ base: "none", sm: "block" }}
          >
            Magyar
          </Box>
          <Flex w="22px" h="22px" borderRadius="full" bg="bob.300" color="white" align="center" justify="center" fontSize="9px" fontWeight="700">
            MM
          </Flex>
        </Flex>

        <Box p={{ base: 3, sm: 5 }}>
          <Text fontWeight="800" fontSize={{ base: "md", sm: "lg" }} color="ink.900" mb={3}>
            Áttekintés
          </Text>

          <Flex align="center" gap={2.5} bg="#FEF3E2" border="1px solid" borderColor="#FCE8C6" borderRadius="lg" px={3} py={2} mb={4}>
            <Flex w="22px" h="22px" borderRadius="md" bg="#FCE8C6" color="#B8860B" align="center" justify="center" flexShrink={0}>
              <Icon as={FaCalendarAlt} boxSize="10px" />
            </Flex>
            <Box minW={0}>
              <Text fontSize="10px" fontWeight="700" color="#8A5A12" noOfLines={1}>
                1 szabadságkérelem vár jóváhagyásra
              </Text>
              <Text fontSize="9px" color="#A9782E" noOfLines={1} display={{ base: "none", sm: "block" }}>
                Nagy Béla · fizetett szabadság · szept. 2–4.
              </Text>
            </Box>
          </Flex>

          <Flex gap={{ base: 1.5, sm: 2.5 }} mb={4} wrap="wrap">
            {TILES.map((t) => (
              <Flex key={t.label} flex="1" minW={{ base: "70px", sm: "90px" }} direction="column" gap={1.5} border="1px solid" borderColor="chalk.300" borderRadius="lg" p={{ base: 2, sm: 3 }}>
                <Flex justify="space-between" align="flex-start">
                  <Text fontSize={{ base: "8px", sm: "10px" }} color="slate.500" noOfLines={2} maxW="70%">
                    {t.label}
                  </Text>
                  <Box w={{ base: "16px", sm: "20px" }} h={{ base: "16px", sm: "20px" }} borderRadius="md" bg={t.swatch} flexShrink={0} />
                </Flex>
                <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="800" color="ink.900">
                  {t.value}
                </Text>
              </Flex>
            ))}
          </Flex>

          <Flex gap={{ base: 4, sm: 6 }} wrap="wrap">
            <Box flex="1" minW="120px">
              <Text fontSize={{ base: "9px", sm: "10px" }} fontWeight="700" color="ink.900" mb={1.5}>
                Közelgő határidők
              </Text>
              <Flex direction="column" gap={1.5}>
                {DEADLINES.map((d) => (
                  <Box key={d.title}>
                    <Text fontSize={{ base: "9px", sm: "xs" }} fontWeight="600" noOfLines={1}>
                      {d.title}
                    </Text>
                    <Text fontSize={{ base: "8px", sm: "10px" }} color="slate.500" noOfLines={1}>
                      {d.who}
                    </Text>
                  </Box>
                ))}
              </Flex>
            </Box>
            <Box flex="1" minW="120px">
              <Text fontSize={{ base: "9px", sm: "10px" }} fontWeight="700" color="ink.900" mb={1.5}>
                Legutóbbi partneresemények
              </Text>
              <Flex direction="column" gap={1.5}>
                {RECENT.map((r) => (
                  <Box key={r.title}>
                    <Text fontSize={{ base: "9px", sm: "xs" }} fontWeight="600" noOfLines={1}>
                      {r.title}
                    </Text>
                    <Text fontSize={{ base: "8px", sm: "10px" }} color="slate.500" noOfLines={1}>
                      {r.who}
                    </Text>
                  </Box>
                ))}
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Flex>
  </Box>
);

export default BobPreviewCard;
